import { Auth } from 'aws-amplify';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { CSRF_TOKEN_HEADER } from '@tanium-cloud/tsp-contracts';
import { getAccessToken } from './auth/getAccessToken';
import { getCSRFToken, removeCSRFToken, updateCSRFToken } from './auth/getCSRFToken';

type Headers = Record<string | 'Authorization', string>;
const AUTH_ERROR_CODES = [401, 403, 408];

/**
 * Gets an instance of Axios for calling the self-service API.
 * This instance will include the access token if there is an authenticated user session.
 */
export const getApiClient = (): AxiosInstance => {
  const apiClient = axios.create();
  apiClient.interceptors.request.use(addAccessTokenInterceptor);
  apiClient.interceptors.request.use(addCsrfTokenInterceptor);
  apiClient.interceptors.response.use(storeCsrfTokenInterceptor, invalidateSessionInterceptor);
  return apiClient;
};

const storeCsrfTokenInterceptor = (res: AxiosResponse) => {
  updateCSRFToken(res);
  return res;
};

/* If the user's session has expired, sign them out and redirect them to the login page */
const invalidateSessionInterceptor = async (error: any) => {
  const statusCode = (error as AxiosError).response?.status;
  if (statusCode !== undefined && AUTH_ERROR_CODES.includes(statusCode)) {
    removeCSRFToken();
    await Auth.signOut();
    window.location.reload();
  }

  return Promise.reject(error);
};

/* Add a CSRF token header to requests a user makes when they are authenticated */
const addCsrfTokenInterceptor = async (config: AxiosRequestConfig) => {
  const headers = config.headers as Headers;
  if (!isCsrfExemptRequest(config)) {
    headers[CSRF_TOKEN_HEADER] = await getCSRFToken();
  }
  return config;
};

const addAccessTokenInterceptor = async (config: AxiosRequestConfig) => {
  const headers = config.headers as Headers;
  const accessToken = await getAccessToken();

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
};

const isCsrfExemptRequest = (config: AxiosRequestConfig): boolean => {
  const exemptCsrfList = [/authconfig/, /csrf/, '/api-public', /idp-setup\/configuration/];
  const isGet = config.method?.toUpperCase() === 'GET';
  const isPublicAPI = !!config.url?.match('/api-public');
  const isExemptPath = exemptCsrfList.some((exemption) => config.url?.match(exemption) !== null);

  return isExemptPath && (isGet || isPublicAPI);
};
